import { Flex, Divider, Spinner } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { ComicDetailsHeader } from "./ComicDetailsHeader";
import { ComicDescription } from "./ComicDescription";
import { ComicDetailsTabs } from "./ComicDetailsTabs";
import { ArtistSection } from "./ArtistSection";
import { Stats } from "../../../screens/reader/components/shared/Stats";
import { getComic, useSlideInMenu } from "../hooks/useSlideInMenu";
import { getAudienceShort } from "../../../screens/reader/utils/comicFacetGroups";
import { ComicDetailsBottomBar } from "./ComicDetailsBottomBar";
import { Comic } from "../../../types";
import { ShowMoreSection } from "../../containers/ShowMoreSection";
import { useQuery, skipToken } from "@tanstack/react-query";
import { formatLargeNumber } from "../../../utils/formatLargeNumber";
import { ComicLabelsCarousel } from "../../comicLabels/ComicLabelsCarousel";
import { CrowdfundingBanner } from "./CrowdfundingBanner";
import { SlideInHeader } from "../components/SlideInHeader";
import { SLIDE_IN_MENUS } from "../../../screens/root/constants";
import { useEntityCache } from "../../../hooks/useEntityCache";
import { trackComicDetailView } from "../../../analytics/events/navigation";
import { mapDetailSource } from "../../../analytics/params/navigation";
import { getNavigationAnalyticsAttribution } from "../../../analytics/params/navigationContextAttribution";
import { shouldTrackDetailViewForMenuInstance } from "../../../analytics/detailViewRegistry";

interface ComicDetailsProps {
  onCloseMenu?: () => void;
}

export function ComicDetails({ onCloseMenu }: ComicDetailsProps) {
  const { comicSlug, navigationContext, goBack, menuInstanceId } = useSlideInMenu();
  const [selectedLangId, setSelectedLangId] = useState<string | undefined>();
  const lastTrackedComicId = useRef<number | undefined>();

  const { saveEntityToCache } = useEntityCache();

  const { data: fetchedComicResponse, isLoading } = useQuery({
    enabled: !!comicSlug,
    queryKey: ["comicDetailsComic", comicSlug],
    queryFn: async () => {
      const response = await getComic(
        comicSlug ?? "placeholder-slug",
        navigationContext,
      );
      const comic = response?.data.payload.results;

      if (comic) {
        saveEntityToCache(comic);
        saveEntityToCache(comic.artist);
      }
      return response;
    },
    networkMode: "offlineFirst",
  });

  const fetchedComic = fetchedComicResponse?.data.payload.results;
  const cacheKey = `${fetchedComic?.entity_type}-${fetchedComic?.id}`;
  const { data: comicData } = useQuery<Comic>({
    enabled: !!fetchedComic,
    queryKey: [cacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });

  useEffect(() => {
    if (!comicData?.id || lastTrackedComicId.current === comicData.id) {
      return;
    }

    if (!shouldTrackDetailViewForMenuInstance(menuInstanceId, "comic", comicData.id)) {
      lastTrackedComicId.current = comicData.id;
      return;
    }

    const source = mapDetailSource(navigationContext);
    const { impressionId, searchQueryId } =
      getNavigationAnalyticsAttribution(navigationContext);
    trackComicDetailView({
      comicId: comicData.id,
      source,
      impressionId,
      searchQueryId,
      searchResultPosition: navigationContext?.cardPosition,
      referrerUrl:
        source === "deep_link" ? document.referrer || undefined : undefined,
    });

    lastTrackedComicId.current = comicData.id;
  }, [comicData?.id, menuInstanceId, navigationContext]);

  if (!comicData || isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        width="100%"
        position="absolute"
        top="0"
        left="0"
      >
        <Spinner color="blaze.blaze" />
      </Flex>
    );
  }

  return (
    <Flex direction="column" height="100%">
      <SlideInHeader
        handleBack={goBack}
        openMenu={SLIDE_IN_MENUS.comicDetails}
        onCloseMenu={onCloseMenu}
      />
      <Flex
        className="styled-scrollbar"
        direction="column"
        width="100%"
        flexGrow={1}
        overflowY="auto"
      >
        {comicData.crowdfunding_campaign ? (
          <CrowdfundingBanner
            crowdfundingCampaign={comicData.crowdfunding_campaign}
          />
        ) : null}
        <Flex
          direction="column"
          justifyContent="flex-start"
          gap="24px"
          p="24px"
        >
          <ComicDetailsHeader
            comicData={comicData}
            selectedLangId={selectedLangId}
          />
          <Stats
            stats={[
              {
                value: formatLargeNumber(comicData?.total_pageviews ?? 0),
                text: t("components.slideInMenu.comicDetailsMenu.views"),
              },
              {
                value: comicData?.total_favorites,
                text: t("components.slideInMenu.comicDetailsMenu.shelves"),
              },
              {
                value: comicData?.total_releases,
                text: t("components.slideInMenu.comicDetailsMenu.releases"),
              },
              {
                value: getAudienceShort(comicData?.comic_facets),
                text: t("components.slideInMenu.comicDetailsMenu.audience"),
              },
            ]}
          />
          <ComicLabelsCarousel comicData={comicData as Comic} />
          {comicData?.artist && (
            <>
              <Divider />
              <ArtistSection artist={comicData?.artist} />
            </>
          )}
          <Divider />
          <ShowMoreSection collapsedHeight={150} expandedMarginBottom="24px">
            <ComicDescription comicData={comicData} />
          </ShowMoreSection>
          <ComicDetailsTabs
            comicData={comicData}
            onLanguageChange={setSelectedLangId}
          />
        </Flex>
      </Flex>
      <ComicDetailsBottomBar comicData={comicData as Comic} />
    </Flex>
  );
}
